/* ototo-ds-collection.js — theme-side filtering + sorting for ds-collection.
 *
 * Every product card carries data-attrs (see ds-collection.liquid). Filtering is
 * OR within a group, AND across groups: pick two worlds -> either world; then add
 * a usage -> must also be that usage. On load the controller counts each facet,
 * reveals only the chips that match something, and shows the count. No network,
 * no store config.
 *
 * Price is a range, not a set: two stacked range inputs bounded by the cheapest
 * and dearest product on the page (whole currency units; `data-price` is cents).
 */
(function () {
  function init(root) {
    if (root.__otColl) return;
    root.__otColl = true;

    var grid = root.querySelector('[data-ot-grid]');
    if (!grid) return;
    var items = Array.prototype.slice.call(grid.querySelectorAll('[data-ot-item]'));
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-ot-filter]'));
    var countEl = root.querySelector('[data-ot-count]');
    var emptyEl = root.querySelector('[data-ot-empty]');
    var clears = Array.prototype.slice.call(root.querySelectorAll('[data-ot-clear]'));
    var barClear = root.querySelector('.ot-coll__clear:not(.ot-coll__clear--inline)');
    var sortEl = root.querySelector('[data-ot-sort]');
    var original = items.slice();

    var active = { flag: {}, world: {}, usage: {} };
    function size(o) { return Object.keys(o).length; }

    /* ── price range ─────────────────────────────────────────────────────── */
    var priceGroup = root.querySelector('[data-group="price"]');
    var pMin = priceGroup && priceGroup.querySelector('[data-ot-price="min"]');
    var pMax = priceGroup && priceGroup.querySelector('[data-ot-price="max"]');
    var pBox = priceGroup && priceGroup.querySelector('[data-ot-price-box]');
    var pOut = priceGroup && priceGroup.querySelector('[data-ot-price-out]');
    var cur = root.getAttribute('data-currency') || '$';
    var floor = 0, ceil = 0, lo = 0, hi = 0;
    function dollars(it) { return (+it.getAttribute('data-price') || 0) / 100; }
    if (pMin && pMax && items.length > 1) {
      var ps = items.map(dollars);
      floor = Math.floor(Math.min.apply(null, ps));
      ceil = Math.ceil(Math.max.apply(null, ps));
    }
    var priceOn = ceil > floor;
    function priceActive() { return priceOn && (lo > floor || hi < ceil); }
    function paintPrice() {
      var span = ceil - floor || 1;
      pBox.style.setProperty('--lo', (lo - floor) / span);
      pBox.style.setProperty('--hi', (hi - floor) / span);
      pOut.textContent = cur + lo + ' – ' + cur + hi;
      pMin.setAttribute('aria-valuetext', cur + lo);
      pMax.setAttribute('aria-valuetext', cur + hi);
      /* Both thumbs parked at the top end: the max input sits on top and would
         swallow every grab, so the min input is lifted while it is up there. */
      pMin.style.zIndex = lo >= ceil - (ceil - floor) * 0.05 ? 3 : '';
    }
    function setPrice(a, b) {
      lo = a; hi = b;
      pMin.value = lo; pMax.value = hi;
      paintPrice();
    }
    if (priceOn) {
      [pMin, pMax].forEach(function (inp) { inp.min = floor; inp.max = ceil; });
      setPrice(floor, ceil);
      pMin.addEventListener('input', function () { setPrice(Math.min(+pMin.value, hi), hi); apply(); });
      pMax.addEventListener('input', function () { setPrice(lo, Math.max(+pMax.value, lo)); apply(); });
    }

    function has(it, group, val) {
      if (group === 'flag') return it.hasAttribute(val === 'new' ? 'data-new' : 'data-best');
      if (group === 'world') return it.getAttribute('data-world') === val;
      if (group === 'usage') return (' ' + (it.getAttribute('data-usage') || '') + ' ').indexOf(' ' + val + ' ') !== -1;
      return false;
    }

    function matches(it) {
      if (priceActive()) { var d = dollars(it); if (d < lo || d > hi) return false; }
      var groups = ['flag', 'world', 'usage'];
      for (var g = 0; g < groups.length; g++) {
        var set = active[groups[g]];
        if (!size(set)) continue;
        var ok = false;
        for (var v in set) { if (set.hasOwnProperty(v) && has(it, groups[g], v)) { ok = true; break; } }
        if (!ok) return false;
      }
      return true;
    }

    function apply() {
      var shown = 0;
      for (var i = 0; i < items.length; i++) {
        var m = matches(items[i]);
        items[i].hidden = !m;
        if (m) shown++;
      }
      if (countEl) countEl.textContent = shown + (shown === 1 ? ' product' : ' products');
      if (emptyEl) emptyEl.hidden = shown !== 0;
      var any = size(active.flag) + size(active.world) + size(active.usage) > 0 || priceActive();
      if (barClear) barClear.hidden = !any;
    }

    /* A filter that leaves one row makes the section shorter than the screen: the
       sticky rail is pushed up with the section's end and the visitor is left
       looking at the FAQ/footer (user report, 2026-10-15). So after a filter
       change, if the top of the results is above the screen, bring it back. */
    var mainEl = root.querySelector('.ot-coll__main') || grid;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function keepInView() {
      var head = document.querySelector('.ot-header');
      var off = head ? Math.max(0, head.getBoundingClientRect().bottom) : 0;
      var top = mainEl.getBoundingClientRect().top;
      if (top < off) window.scrollBy({ top: top - off - 12, behavior: reduce ? 'auto' : 'smooth' });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var group = chip.getAttribute('data-ot-filter');
        var val = chip.getAttribute('data-value');
        if (active[group][val]) { delete active[group][val]; chip.setAttribute('aria-pressed', 'false'); }
        else { active[group][val] = 1; chip.setAttribute('aria-pressed', 'true'); }
        apply();
        keepInView();
      });
    });

    /* Count every facet; reveal the chips that match a product, with a tally.
       A whole group with no live chip is hidden so the bar stays tidy. */
    chips.forEach(function (chip) {
      var group = chip.getAttribute('data-ot-filter');
      var val = chip.getAttribute('data-value');
      var n = 0;
      for (var i = 0; i < items.length; i++) { if (has(items[i], group, val)) n++; }
      if (n > 0) {
        chip.hidden = false;
        var nEl = chip.querySelector('[data-ot-chip-n]');
        if (nEl) nEl.textContent = n;
      }
    });
    /* Reveal a World/Usage group only if it offers a real CHOICE — more than one
       live chip. A single option (e.g. one world on a themed collection) filters
       nothing, so the whole group is dropped. These start `hidden`, so this must
       assign both ways. */
    root.querySelectorAll('.ot-coll__fgroup[data-group]:not([data-group="price"])').forEach(function (fg) {
      fg.hidden = fg.querySelectorAll('.ot-fchip:not([hidden])').length <= 1;
    });
    if (priceGroup) priceGroup.hidden = !priceOn;

    function clearAll() {
      active = { flag: {}, world: {}, usage: {} };
      chips.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
      if (priceOn) setPrice(floor, ceil);
      apply();
      keepInView();
    }
    clears.forEach(function (c) { c.addEventListener('click', clearAll); });

    function sortBy(key) {
      var arr;
      if (key === 'featured') {
        arr = original.slice();
      } else {
        arr = items.slice().sort(function (a, b) {
          if (key === 'price-asc') return (+a.getAttribute('data-price')) - (+b.getAttribute('data-price'));
          if (key === 'price-desc') return (+b.getAttribute('data-price')) - (+a.getAttribute('data-price'));
          if (key === 'date-desc') return (+b.getAttribute('data-date')) - (+a.getAttribute('data-date'));
          if (key === 'title-asc') return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || '');
          return 0;
        });
      }
      var frag = document.createDocumentFragment();
      arr.forEach(function (it) { frag.appendChild(it); });
      grid.appendChild(frag);
    }
    if (sortEl) sortEl.addEventListener('change', function () { sortBy(sortEl.value); });
    /* The slider re-filters live on `input`; re-centre only once the thumb is let go. */
    if (priceOn) [pMin, pMax].forEach(function (inp) { inp.addEventListener('change', keepInView); });

    apply();
  }

  function boot() { document.querySelectorAll('[data-ot-coll]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
